import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout, { FEATURE_MAP } from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import SOPVaultPage from "./pages/SOPVaultPage";
import BriefingPage from "./pages/BriefingPage";
import TasksPage from "./pages/TasksPage";
import ReviewsPage from "./pages/ReviewsPage";
import VenuesPage from "./pages/VenuesPage";
import MediaStudioPage from "./pages/MediaStudioPage";
import AcademyPage from "./pages/AcademyPage";
import HandoverPage from "./pages/HandoverPage";
import DeveloperAdminPage from "./pages/DeveloperAdminPage";
import AccountPage from "./pages/AccountPage";
import DigestPage from "./pages/DigestPage";
import RosterPage from "./pages/RosterPage";
import PermissionsPage from "./pages/PermissionsPage";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400 font-heading font-bold">Loading Practitality…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && location.pathname !== "/account") return <Navigate to="/account" replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user?.role === "developer") return <Navigate to="/developer-admin" replace />;
  const on = (k) => user?.features?.[k] !== false;
  if (["staff", "supervisor"].includes(user?.role)) {
    const order = [["/briefing", "briefings"], ["/tasks", "tasks"], ["/roster", "roster"],
                   ["/sops", "sops"], ["/academy", "academy"]];
    const dest = order.find(([, k]) => on(k))?.[0] || "/account";
    return <Navigate to={dest} replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function FeatureGuard({ path, children }) {
  const { user } = useAuth();
  const key = FEATURE_MAP[path];
  if (user?.role !== "developer" && key && user?.features?.[key] === false) return <Navigate to="/" replace />;
  return children;
}

function Gated({ path, children }) {
  return <FeatureGuard path={path}>{children}</FeatureGuard>;
}

function DevOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "developer") return <Navigate to="/" replace />;
  return children;
}

function RoleGuard({ roles, children }) {
  const { user } = useAuth();
  if (!user || roles.includes(user.role)) return children;
  return <Navigate to={user.role === "developer" ? "/developer-admin" : ["staff", "supervisor"].includes(user.role) ? "/briefing" : "/dashboard"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/dashboard" element={<RoleGuard roles={["admin", "gm", "manager"]}><DashboardPage /></RoleGuard>} />
            <Route path="/sops" element={<Gated path="/sops"><SOPVaultPage /></Gated>} />
            <Route path="/sop-studio" element={<Navigate to="/sops" replace />} />
            <Route path="/voice-sop" element={<Navigate to="/sops" replace />} />
            <Route path="/brand-studio" element={<Navigate to="/sops" replace />} />
            <Route path="/briefing" element={<Gated path="/briefing"><BriefingPage /></Gated>} />
            <Route path="/tasks" element={<Gated path="/tasks"><TasksPage /></Gated>} />
            <Route path="/reviews" element={<RoleGuard roles={["admin", "gm", "manager"]}><Gated path="/reviews"><ReviewsPage /></Gated></RoleGuard>} />
            <Route path="/leaderboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="/venues" element={<RoleGuard roles={["admin", "gm", "manager"]}><VenuesPage /></RoleGuard>} />
            <Route path="/permissions" element={<RoleGuard roles={["admin", "gm"]}><PermissionsPage /></RoleGuard>} />
            <Route path="/media" element={<RoleGuard roles={["manager", "developer"]}><Gated path="/media"><MediaStudioPage /></Gated></RoleGuard>} />
            <Route path="/academy" element={<Gated path="/academy"><AcademyPage /></Gated>} />
            <Route path="/handover" element={<Gated path="/handover"><HandoverPage /></Gated>} />
            <Route path="/exec-handovers" element={<Navigate to="/handover" replace />} />
            <Route path="/developer-admin" element={<DevOnly><DeveloperAdminPage /></DevOnly>} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/digest" element={<RoleGuard roles={["admin"]}><Gated path="/digest"><DigestPage /></Gated></RoleGuard>} />
            <Route path="/roster" element={<Gated path="/roster"><RosterPage /></Gated>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
